import { PropsWithChildren, createContext, useContext } from 'react';

import { useRouteRef } from '@backstage/core-plugin-api';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { WorkflowInstanceFragment } from '@spotify/confidence-graphql';
import { PermissionUtils } from '@spotify/plugin-confidence-permissions-react';

import { workflowInstanceRouteRef } from '../../routes';
import { useWorkflowContext } from '../WorkflowContext';

type WorkflowInstanceContext<T = {}> = {
  href: string;
  route: (subpage: string) => string;
  canEdit: boolean;
  isDraft: boolean;
  isLive: boolean;
} & WorkflowInstanceFragment &
  T;

const context = createContext<WorkflowInstanceContext | undefined>(undefined);

export const WorkflowInstanceContextProvider = <T,>({
  value,
  children,
}: PropsWithChildren<{ value: WorkflowInstanceFragment & T }>) => {
  const { workflowId: workflow } = useWorkflowContext();

  const id = extractLastNameComponent(value?.name);

  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const href = workflowInstanceRoute?.({
    workflow,
    id: id!,
  });

  const canEdit = PermissionUtils.hasRelation(value, 'can_edit');

  const isDraft = value.state === 'draft';
  const isLive = value.state === 'live';

  const route = (subpage: string) => `${href}/${subpage}`;

  return (
    <context.Provider
      value={{
        ...value,
        href,
        route,
        canEdit,
        isDraft,
        isLive,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useWorkflowInstanceContext = <
  T = {},
>(): WorkflowInstanceContext<T> => {
  const value = useContext(context);

  if (!value) {
    throw new Error(
      'useWorkflowInstance must be used within a WorkflowInstanceProvider',
    );
  }

  return value as WorkflowInstanceContext<T>;
};
